import React from "react"
import { View, Text, StyleSheet } from "react-native"
import { FontAwesome5 } from "@expo/vector-icons"
import { theme } from "@common/theme/theme"
import { elide } from "@common/utils/activationSuiteDisplay"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import { IconImageM } from "@common/components/icons/IconsM"
import { IS_WEB } from "@common/utils/mobileUtils"
import { cleanMediaUrl } from "@data/constants/apiConstants"
import SummerEyebrow from "./SummerEyebrow"
import TutorShowcaseCard from "./TutorShowcaseCard"

// Pile de visages "et N autres" : petits cercles ~34px qui se chevauchent, avec
// un anneau blanc (comme le proto). Image brute (pas le traitement tuteur AvatarM
// nœud-papillon) — on veut juste des visages empilés et propres.
const STACK_AVATAR_SIZE = 34
export const StackAvatar = ({ uri, size = STACK_AVATAR_SIZE }) => {
    const inner = size - 4
    return (
        <View
            style={[
                styles.avatarRing,
                { width: size, height: size, borderRadius: size / 2 },
            ]}
        >
            {IS_WEB ? (
                <img
                    src={cleanMediaUrl(uri)}
                    style={{
                        width: inner,
                        height: inner,
                        borderRadius: inner / 2,
                        objectFit: "cover",
                    }}
                />
            ) : (
                <IconImageM
                    uri={cleanMediaUrl(uri)}
                    width={inner}
                    height={inner}
                    resizeMode="cover"
                    style={{ borderRadius: inner / 2, overflow: "hidden" }}
                />
            )}
        </View>
    )
}

/**
 * Contenu présentationnel de l'étape "aha moment tuteur humain" — entre le
 * programme (summer/plan) et le checkout 1 € (summer/slots-checkout). Montre
 * qu'il y a de vrais humains derrière l'offre : un compte de masteurs qui
 * correspondent au profil de l'enfant, 2 cartes vitrine + une pile de visages
 * "et N autres", et un bloc de réassurance "Nos engagements" sur la procédure
 * d'attribution.
 *
 * Pur props (data depuis le matching backend). Étape NON bloquante : sur erreur
 * ou réponse vide, l'écran passe `failed` → on masque le compte (pas de chiffre
 * douteux) et on remplace le titre par une ligne de repli neutre, mais on garde
 * le bloc garanties et le CTA reste actif (cf. SummerTutorsScreen).
 */

const SummerTutorsContent = ({
    firstname,
    count,
    total,
    tutors,
    moreAvatars,
    failed,
    // Eyebrow de marque : défaut « ☀️ Cours d'été » pour le funnel été ; le
    // funnel stage (qui réutilise ce contenu) passe son propre libellé (nom du
    // template + icône stage) pour ne pas afficher « Cours d'été » chez lui.
    eyebrowLabel,
    /** Tailles des visages — le funnel stage les veut plus grands que l'été. */
    cardAvatarSize,
    stackAvatarSize,
    /**
     * Le sous-titre : absent = la maîtrise des matières (l'été), `null` = rien
     * (le stage, où la recherche vient d'être racontée), une phrase = celle du
     * funnel appelant — l'inscription y dit QUI attribue le masteur, la question
     * que le parent se pose devant des visages qu'il croit devoir choisir. Une
     * phrase fournie tient aussi en repli : ce qui se passe ensuite ne dépend
     * pas du matching.
     */
    subtitle,
    /** Le troisième engagement : celui de l'été par défaut, le stage a le sien. */
    thirdGuarantee = {
        icon: "calendar-check",
        label: "Attribué avant le 1ᵉʳ cours",
    },
}) => {
    const child = firstname || "votre enfant"
    const list = tutors || []
    // Visages de la pile "et N autres" : autres masteurs avec photo qui couvrent le
    // panier, au-delà des 2 cartes vitrine (cf. backend `more_avatars`).
    const avatars = moreAvatars || []
    // "et N autres" = reste du vivier au-delà des cartes vitrine. On s'appuie sur
    // `total` (vivier complet) quand fourni, sinon sur `count` (matching strict).
    const pool = Math.max(count || 0, total || 0)
    const othersCount = Math.max(0, pool - list.length)
    // La phrase par défaut vante le matching : elle n'a pas sa place en repli.
    const subtitleLine =
        subtitle === undefined
            ? failed
                ? null
                : `Choisis pour leur maîtrise des matières du programme ${elide(child)}.`
            : subtitle

    return (
        <>
            <SummerEyebrow label={eyebrowLabel} />

            {failed ? (
                // Repli gracieux : pas de chiffre (le matching a échoué / est vide),
                // mais on reste rassurant et le CTA continue vers le checkout.
                <Text style={styles.title}>
                    Des masteurs prêts à accompagner {child}
                </Text>
            ) : (
                <Text style={styles.title}>
                    <Text style={styles.titleNumber}>{count} masteurs</Text>{" "}
                    correspondent aux besoins {elide(child)}
                </Text>
            )}
            {subtitleLine ? (
                <Text style={styles.subtitle}>{subtitleLine}</Text>
            ) : null}

            {list.map((t) => (
                <TutorShowcaseCard
                    key={String(t.id)}
                    tutor={t}
                    avatarSize={cardAvatarSize}
                />
            ))}

            {othersCount > 0 ? (
                <View style={styles.othersRow}>
                    {avatars.length > 0 ? (
                        <View style={styles.avatarStack}>
                            {avatars.map((avatar, i) => (
                                <View
                                    key={`${i}-${avatar}`}
                                    style={i > 0 ? styles.avatarOverlap : null}
                                >
                                    <StackAvatar
                                        uri={avatar}
                                        size={stackAvatarSize}
                                    />
                                </View>
                            ))}
                        </View>
                    ) : null}
                    <Text style={styles.othersLine}>
                        et {othersCount} autres masteurs disponibles
                    </Text>
                </View>
            ) : null}

            <View style={styles.guarantees}>
                <Text style={styles.guaranteesTitle}>Nos engagements</Text>
                <View style={styles.badgeRow}>
                    <View style={[styles.gBadge, styles.gBadgePrimary]}>
                        <FontAwesome5
                            name="exchange-alt"
                            solid
                            size={11}
                            color={theme.colors.white}
                        />
                        <Text
                            style={[
                                styles.gBadgeText,
                                styles.gBadgeTextPrimary,
                            ]}
                        >
                            Changez de masteur à tout moment
                        </Text>
                    </View>
                    <View style={styles.gBadge}>
                        <FontAwesome5
                            name="shield-alt"
                            solid
                            size={11}
                            color={theme.colors.success}
                        />
                        <Text style={styles.gBadgeText}>
                            Masteurs vérifiés & formés
                        </Text>
                    </View>
                    <View style={styles.gBadge}>
                        <FontAwesome5
                            name={thirdGuarantee.icon}
                            solid
                            size={11}
                            color={theme.colors.success}
                        />
                        <Text style={styles.gBadgeText}>
                            {thirdGuarantee.label}
                        </Text>
                    </View>
                </View>
            </View>
        </>
    )
}

const styles = StyleSheet.create({
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 6,
    },
    titleNumber: { color: theme.colors.primary },
    subtitle: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        marginBottom: 18,
    },

    // Bloc "et N autres" : pile de visages (more_avatars) + libellé. Quand le
    // backend ne renvoie pas d'avatars, la pile est masquée → libellé seul.
    othersRow: {
        flexDirection: "row",
        alignItems: "center",
        marginTop: 2,
        marginBottom: 20,
    },
    avatarStack: {
        flexDirection: "row",
        alignItems: "center",
        marginRight: 10,
    },
    // Anneau blanc autour de chaque visage (le chevauchement laisse voir l'anneau).
    avatarRing: {
        width: STACK_AVATAR_SIZE,
        height: STACK_AVATAR_SIZE,
        borderRadius: STACK_AVATAR_SIZE / 2,
        backgroundColor: theme.colors.white,
        borderWidth: 2,
        borderColor: theme.colors.white,
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        // Ombre douce pour détacher la pile du fond.
        shadowColor: "#101d40",
        shadowOpacity: 0.1,
        shadowRadius: 3,
        shadowOffset: { width: 0, height: 1 },
        elevation: 1,
    },
    avatarOverlap: { marginLeft: -12 },
    othersLine: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.primary,
        flexShrink: 1,
    },

    // Bloc garanties (le "changez à tout moment" en badge primaire mis en avant).
    guarantees: {
        backgroundColor: theme.colors.successLight || "#f1fff3",
        borderRadius: 12,
        padding: 14,
        marginTop: 2,
    },
    guaranteesTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 13,
        color: theme.colors.successDark || theme.colors.primaryDark,
        marginBottom: 10,
    },
    badgeRow: { flexDirection: "row", flexWrap: "wrap" },
    gBadge: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.white,
        borderWidth: 1,
        borderColor: theme.colors.success,
        borderRadius: 999,
        paddingVertical: 5,
        paddingHorizontal: 10,
        marginRight: 8,
        marginBottom: 8,
    },
    gBadgePrimary: {
        backgroundColor: theme.colors.success,
        borderColor: theme.colors.success,
    },
    gBadgeText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.successDark || theme.colors.success,
        marginLeft: 6,
    },
    gBadgeTextPrimary: { color: theme.colors.white },
})

export default SummerTutorsContent
